/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import _forEach from 'lodash/forEach';
import Swiper from 'swiper';
import {
  processSubtab,
} from '../../services/processImagery/processSubtab';
import FavoritesService from '../../components/Favorites/Favorites.service';
import ImagePreLoader, { FailedImagesRequestError } from '../../services/imagesPreloader.service';
import utilityService from '../../services/utility.service';
import ConfigApplicationSettings from '../../config/application.config';
import bnd3Service from '../../services/bnd3.service';
import d3 from '../../globalexports/d3.export';
import avwxService from './Imagery.service';
import AppConst from '../../constants/app.constant';
import router from '../../router';

let loadingImagesLock = false;
let svg;
const maxZoomDepth = 14;

const imageryUtility = {};
imageryUtility.setVmContext = (vm) => {
  imageryUtility.vm = vm;
};
imageryUtility.setImagerySwiper = () => {
  imageryUtility.imagerySwiper = new Swiper('.imagery-swiper-container', {
    allowTouchMove: false,
  });
};
imageryUtility.getSVGContainerID = () => {
  if (ConfigApplicationSettings.imageryViewUseSubnav) {
    return 'imagerysvgsubnav';
  }
  return 'imagerysvg';
};
imageryUtility.cleanSVG = () => {
  d3.select(`#${imageryUtility.getSVGContainerID()} svg`).remove();
};
imageryUtility.calculateAspectRatioFit = function (
  srcWidth,
  srcHeight,
  maxWidth,
  maxHeight,
) {
  const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
  return {
    width: srcWidth * ratio,
    height: srcHeight * ratio,
  };
};
imageryUtility.loadSvgImagery = function (failedImageUrls) {
  const svgContainerId = imageryUtility.getSVGContainerID();
  // If atleast one image is available then only load imagery section
  if (imageryUtility.vm.imageLocations && imageryUtility.vm.imageLocations[0]) {
    let imageToUseForSizeRef = imageryUtility.vm.imageLocations[0];
    if (failedImageUrls) {
      let i = 0;
      while (i < imageryUtility.vm.imageLocations.length) {
        if (failedImageUrls.indexOf(imageryUtility.vm.imageLocations[i]) === -1) {
          imageToUseForSizeRef = imageryUtility.vm.imageLocations[i];
          break;
        }
        i += 1;
      }
    }
    avwxService
      .returnImageSizeRef(imageToUseForSizeRef)
      .then((imageRefRes) => {
        // width = bnd3Service.getSelectionWidth(d3.select(".imagery-content"));
        const availableSpace = utilityService.getImageryAvailableSpace(
          imageryUtility.vm.imageLocations.length,
          ConfigApplicationSettings.imageryViewUseSubnav,
          ConfigApplicationSettings.showMobileView,
        );
        const imageDimensions = imageryUtility.calculateAspectRatioFit(
          imageRefRes.naturalWidth,
          imageRefRes.naturalHeight,
          availableSpace.width,
          availableSpace.height,
        );
        const imgHeight = imageDimensions.height;
        const imgWidth = imageDimensions.width;
        const {
          width,
        } = availableSpace;
        const {
          height,
        } = availableSpace;
        const translate0 = [
          (availableSpace.width - imgWidth) / 2,
          (availableSpace.height - imgHeight) / 2,
        ];
        const scale0 = 1; // Initial offset & scale
        imageryUtility.cleanSVG();
        imageryUtility.vm.lastZoom = 1;
        svg = d3
          .select(`#${svgContainerId}`)
          .append('svg')
          .attr('width', `${width}px`)
          .style('margin-top', imageryUtility.vm.hideBottomAxis ? '0px' : '2px')
          .attr(
            'height',
            `${height
              - (imageryUtility.vm.imageLocations && imageryUtility.vm.imageLocations.length === 1 ? 6 : 0)}px`,
          );

        function resetZoomOnMaxScale() {
          if (imageryUtility.vm.lastZoom === maxZoomDepth) {
            setTimeout(() => {
              imageryUtility.vm.resetted();
              setTimeout(() => {
                imageryUtility.vm.lastZoom = 1;
              });
            });
          }
        }

        function zoomed(event) {
          imageryUtility.vm.lastZoom = event.transform.k;
          svg.attr('transform', event.transform);
        }

        svg
          .append('rect')
          .attr('fill', '#000')
          .attr('width', `${width}px`)
          .attr('height', `${height}px`);
        const zoom = d3
          .zoom()
          .scaleExtent([1, maxZoomDepth])
          .translateExtent([
            [0, 0],
            [width, height],
          ])
          .on('zoom', zoomed);
        const errorImgWidth = 20;
        svg = svg
          .append('g')
          .attr('id', 'tempG')
          .attr(
            'transform',
            `translate(${translate0[0]},${translate0[1]})scale(${scale0})`,
          )
          .call(zoom)
          .append('g')
          .on('dblclick', resetZoomOnMaxScale)
          .dblTap(resetZoomOnMaxScale);
        const failedImages = [];
        svg
          .selectAll('g')
          .data(imageryUtility.vm.imageLocations)
          .enter()
          .append('g')
          .attr('id', (...args) => `${svgContainerId}-accordImg${args[1]}`)
          .attr('class', 'accordImages')
          .style('display', 'none')
          .selectAll('altitudeImages')
          .data((d) => (d.constructor.name !== 'Array' ? [d] : d))
          .enter()
          .append('image')
          .attr('loading', 'lazy')
          .attr('id', (d) => {
            const urlParams = new URLSearchParams(d);
            const altParam = urlParams.get('alt');
            return `image-${altParam !== 'undefined' ? altParam : '0'}`;
          })
          .style('opacity', (d, k) => {
            if (failedImageUrls.indexOf(d) > -1) {
              failedImages.push(k);
              return 1;
            }
            return 1;
          })
          .attr('width', (d) => {
            if (failedImageUrls.indexOf(d) > -1) {
              return `${errorImgWidth}px`;
            }
            return `${imgWidth}px`;
          })
          .attr('height', (d) => {
            if (failedImageUrls.indexOf(d) > -1) {
              return `${errorImgWidth}px`;
            }
            return `${imgHeight}px`;
          })

          .attr('xlink:href', (d, idx) => {
            const urlParams = new URLSearchParams(d);
            const imageIdx = urlParams.get('imageIdx');
            return (imageIdx === '0' || idx === 0) ? d : null;
          })
          .attr('data-href', (d) => d)
          .attr('x', (d) => {
            if (failedImageUrls.indexOf(d) > -1) {
              return (width - errorImgWidth) / 2 - translate0[0];
            }
            return undefined;
          })
          .attr('y', (d) => {
            if (failedImageUrls.indexOf(d) > -1) {
              return (
                height / 2 - translate0[1] - errorImgWidth / 2 - errorImgWidth
              );
            }
            return undefined;
          })
          .style('display', (d, idx) => (idx === 0 ? null : 'none'));
        _forEach(failedImages, (value) => {
          d3.select(`#${imageryUtility.getSVGContainerID()}-accordImg${value}`)
            .append('text')
            .text(AppConst.imageryLoadFailed404PlaceholderText)
            .attr('x', (...args) => {
              const selfEle = utilityService.getSelfElement(args);
              return (
                (width - bnd3Service.getSelectionWidth(d3.select(selfEle)))
                / 2
                - translate0[0]
              );
            })
            .attr('y', (...args) => {
              const selfEle = utilityService.getSelfElement(args);
              return (
                height / 2
                - translate0[1]
                + bnd3Service.getSelectionHeight(d3.select(selfEle)) / 2
              );
            })
            .attr('fill', '#fff');
        });
        if (imageryUtility.vm.imageLocations && imageryUtility.vm.imageLocations.length === 1) {
          // if there is only 1 image and no loops
          imageryUtility.vm.currentVisibleImage = 0;
        }
        imageryUtility.vm.showHideImage();
        imageryUtility.vm.resetted = () => {
          d3.select('#tempG')
            .transition()
            .duration(1000)
            .call(zoom.transform, d3.zoomIdentity);
        };
      });
    imageryUtility.vm.settings.currentVisibleImage = imageryUtility.vm.currentVisibleImage;
    imageryUtility.vm.showHideImage();
    if (imageryUtility.vm.$refs.etdSliderApiMobileNav) {
      if (imageryUtility.vm.imageryViewUseSubnav) {
        imageryUtility.vm.$refs.etdSliderApiMobileNav.refresh(
          imageryUtility.vm.settings,
          null,
          imageryUtility.vm.imageLocations,
          imageryUtility.vm.dateFormat,
          true,
        );
      } else {
        imageryUtility.vm.$refs.etdSliderApiAccordion.refresh(
          imageryUtility.vm.settings,
          null,
          imageryUtility.vm.imageLocations,
          imageryUtility.vm.dateFormat,
          true,
        );
      }
    }
  }
};
imageryUtility.loadImages = () => {
  if (!loadingImagesLock) {
    loadingImagesLock = true;
    imageryUtility.vm.resetCachebuster();
    // console.log(d3.select("#progress-radial-avwx-secondary svg"));
    d3.select('#progress-radial-avwx-secondary svg').remove();
    d3.select('#progress-radial-avwx-primary svg').remove();
    imageryUtility.attachedLoaderPrimary = bnd3Service.attachLoader(
      'progress-radial-avwx-primary',
    );
    imageryUtility.attachedLoaderSecondary = bnd3Service.attachLoader(
      'progress-radial-avwx-secondary',
    );
    imageryUtility.vm.isLoading = true;
    imageryUtility.cleanSVG();
    imageryUtility.vm.sliderLabel = null;
    imageryUtility.vm.percentLoaded = Math.floor(Math.random() * 6) + 1;
    imageryUtility.vm.update(imageryUtility.vm.percentLoaded / 100);
    imageryUtility.vm.isMarkedFavorite = FavoritesService.isMarkedFavorite(imageryUtility
      .vmsettings);
    imageryUtility.vm.imageLocations = [];
    let validationInfo = null;
    if (avwxService.selectedSubtab && avwxService.selectedSubtab.i) {
      const {
        subtabImages,
        validTimestamps,
        imageUrlPlaceholder,
        imageLocations,
      } = processSubtab(avwxService.selectedSubtab, imageryUtility.vm
        .cacheBuster);
      avwxService.selectedSubtab.imageUrlPlaceholder = imageUrlPlaceholder;
      avwxService.selectedSubtab.i = subtabImages;
      validationInfo = validTimestamps;
      imageryUtility.vm.imageLocations = imageLocations;
    }
    avwxService.validationInfo = validationInfo;
    if (avwxService.selectedSubtab) {
      avwxService.selectedSubtab.vData = validationInfo;
      if (
        imageryUtility.vm.imageLocations
            && imageryUtility.vm.imageLocations.length === 1
            && validationInfo
            && validationInfo.length === 1
            && imageryUtility.vm.avwxServiceRef
            && imageryUtility.vm.avwxServiceRef.selectedSubtab
      ) {
        imageryUtility.vm.sliderLabel = avwxService.getImageryValidInfo(
          imageryUtility.vm.avwxServiceRef.selectedSubtab.vData[0],
        );
      }
      imageryUtility.vm.errorLoadingImage = false;

      const progressFn = (percent) => {
        loadingImagesLock = false;
        imageryUtility.vm.percentLoaded = percent;
        imageryUtility.vm.update(imageryUtility.vm.percentLoaded / 100);
      };

      new ImagePreLoader(imageryUtility.vm.imageLocations, progressFn)
        .request()
        .then(() => {
          imageryUtility.vm.isLoading = false;
          imageryUtility.vm.isSuccessful = true;
          loadingImagesLock = false;
          imageryUtility.loadSvgImagery([]);
        })
        .catch((error) => {
          if (!(error instanceof FailedImagesRequestError)) {
            throw error;
          }
          loadingImagesLock = false;
          if (imageryUtility.vm.imageLocations.length > error.failedImageUrls.length) {
            imageryUtility.vm.isLoading = false;
            imageryUtility.vm.isSuccessful = true;
            imageryUtility.loadSvgImagery(error.failedImageUrls);
          } else {
            imageryUtility.vm.isLoading = false;
            imageryUtility.vm.isSuccessful = false;
            imageryUtility.vm.errorLoadingImage = true;
            imageryUtility.vm.imageryLoadFailedPlaceholderText = AppConst.imageryLoadFailedPlaceholderText;
            // avwxCtrl.retryImageryLoader();
          }
        });
    }
  }
};
imageryUtility.updateSwiper = () => {
  if (imageryUtility.imagerySwiper && imageryUtility.imagerySwiper.update) {
    imageryUtility.imagerySwiper.update();
  }
};
imageryUtility.redrawOnResize = (imagerySwiper) => {
  if (imageryUtility.vm) {
    imageryUtility.vm.hideBottomAxis = utilityService.hideBottomAxis();
  }
  setTimeout(() => {
    if (router.currentRoute.value.name === AppConst.stateMap.imagery.key) {
      imageryUtility.vm.imageryViewUseSubnav = ConfigApplicationSettings.imageryViewUseSubnav;
      imageryUtility.vm.showMobileView = ConfigApplicationSettings.showMobileView;
      if (!imageryUtility.vm.errorLoadingImage) {
        imageryUtility.loadSvgImagery([]);
        setTimeout(() => {
          if (imagerySwiper) {
            imageryUtility.updateSwiper();
          }
        }, 200);
      }
    }
  }, 0);
};
imageryUtility.slideToWhichSlide = (slideTo, imagerySwiper) => {
  imageryUtility.updateSwiper();
  if (
    imagerySwiper
    && imagerySwiper.slides
    && imagerySwiper.slides.length
    && slideTo < imagerySwiper.slides.length
  ) {
    imagerySwiper.slideTo(slideTo);
  }
};
imageryUtility.getActiveIndex = (imagerySwiper) => imagerySwiper.activeIndex;
export default imageryUtility;
